import { useMemo, useState } from 'react'
import { Eye, Search, FileText, CheckCircle2, XCircle, Clock, Info } from 'lucide-react'
import clsx from 'clsx'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import FacturaPreviewPDF from '../../../../../table/typesExportPDF/FacturaPreviewPDF'
import { getDteAbbreviation } from '../../utils/dteLabels'
import { formatDate } from '../../../../../../../components/utils/formatter'
import LucaModal from '../../../../../ui/LucaModal'
import Button from '../../../../../ui/CustomButton'
import { FacturaAPI } from '../../../../../../../api/facturas.api'

const formatCLP = value =>
  Number(value || 0).toLocaleString('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })

const ActionButton = ({ label, onClick, tone = 'slate', children }) => {
  const toneClass =
    tone === 'red'
      ? 'text-red-600 hover:bg-red-50'
      : tone === 'blue'
        ? 'text-blue-600 hover:bg-blue-50'
        : 'text-slate-600 hover:bg-slate-100'

  return (
    <button
      type='button'
      onClick={onClick}
      className={`rounded-lg p-2 transition ${toneClass}`}
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  )
}

const DTE_NAMES = {
  33: 'Factura Electrónica',
  34: 'Factura Exenta',
  61: 'Nota de Crédito',
  56: 'Nota de Débito'
}

// success = emitido; awaiting_confirmation = se envió bien pero el SII todavía no
// confirmó (NO es un rechazo); cualquier otro valor = rechazo real.
const getRowStatus = doc => {
  const raw = String(doc?.estado_final || '').toLowerCase()
  if (raw === 'success' || doc?.documento_emitido?.webhook_payload?.event === 'document.issued') {
    return 'success'
  }
  if (raw === 'awaiting_confirmation') return 'awaiting_confirmation'
  return 'error'
}

const ROW_STATUS_STYLES = {
  success: {
    label: 'Emitido',
    badgeClass: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    motivoClass: 'text-slate-300'
  },
  awaiting_confirmation: {
    label: 'Pendiente',
    badgeClass: 'bg-amber-50 text-amber-600 border-amber-100',
    motivoClass: 'text-amber-500'
  },
  error: {
    label: 'Rechazado',
    badgeClass: 'bg-rose-50 text-rose-600 border-rose-100',
    motivoClass: 'text-rose-400'
  }
}

export default function DetalleEmisionMasiva({
  currentBusiness,
  documents,
  isLoading,
  calculateTotals
}) {
  const facturaApi = useMemo(() => new FacturaAPI(), [])
  const [searchTerm, setSearchTerm] = useState('')
  const [sortOrder, setSortOrder] = useState('desc')
  // Estado para el modal de vista previa (emitido = PDF real, rechazado = PDF local)
  const [previewDoc, setPreviewDoc] = useState(null)            // rechazados
  const [pdfModal, setPdfModal] = useState({ open: false, url: null, loading: false, error: null }) // emitidos

  const handleViewPdf = async (doc) => {
    const docId = doc?.documento_emitido?.webhook_payload?.document_id || doc?.documento_emitido?.documentId
    if (!docId) return
    setPdfModal({ open: true, url: null, loading: true, error: null })
    try {
      const response = await facturaApi.getDocumentoArchivos(docId)
      const pdfUrl = response?.data?.pdf
      if (!pdfUrl) throw new Error('Sin URL de PDF')
      setPdfModal({ open: true, url: pdfUrl, loading: false, error: null })
    } catch {
      setPdfModal({ open: true, url: null, loading: false, error: 'No se pudo obtener el PDF del documento.' })
    }
  }

  const metrics = useMemo(() => {
    const total = documents.length
    let success = 0
    let pending = 0
    documents.forEach(doc => {
      const status = getRowStatus(doc)
      if (status === 'success') success++
      else if (status === 'awaiting_confirmation') pending++
    })
    const rejected = total - success - pending
    return { total, success, pending, rejected }
  }, [documents])

  const filteredDocs = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    let filtered = documents

    if (term) {
      filtered = documents.filter(doc => {
        const receiver = doc.document_receiver || {}
        return [
          receiver.rut,
          receiver.business_name,
          receiver.address,
          doc.date_issued,
          doc.dte_type?.code
        ]
          .filter(Boolean)
          .some(value => String(value).toLowerCase().includes(term))
      })
    }

    return [...filtered].sort((a, b) => {
      const da = new Date(a.date_issued || 0).getTime()
      const db = new Date(b.date_issued || 0).getTime()
      return sortOrder === 'desc' ? db - da : da - db
    })
  }, [documents, searchTerm, sortOrder])

  const toggleSortDate = () => setSortOrder(prev => (prev === 'desc' ? 'asc' : 'desc'))

  const styles = {
    table: 'w-full border-collapse text-[0.725rem] leading-tight font-sans',
    thead: 'sticky top-0 z-10 bg-slate-50/90 backdrop-blur-md',
    th: 'py-4 px-4 text-center border-b border-slate-200 uppercase text-[0.6rem] font-bold text-slate-500 tracking-wider',
    tr: 'border-b border-slate-50 hover:bg-slate-50/50 transition-colors',
    td: 'px-4 py-2.5 align-middle',
    boldText: 'font-bold text-[0.7rem] text-slate-900',
    text: 'text-[0.64rem] font-medium text-slate-500',
    secondaryText: 'text-[0.6rem] text-slate-400'
  }

  return (
    <div className='flex flex-col h-full min-h-0 animate-in fade-in slide-in-from-bottom-2 duration-500'>
      <header className='mb-3'>
        <h2 className='text-xl font-bold text-slate-900 mb-1.5 tracking-tight leading-none'>
          Resultado de la emisión
        </h2>
        <p className='text-slate-500 text-[0.7rem] mt-1 font-medium leading-relaxed max-w-2xl'>
          Consulta el estado final de los documentos programados.
        </p>
      </header>

      <div className='grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4'>
        {/* Total */}
        <div className='group relative bg-slate-50 border border-slate-200 rounded-xl p-3 overflow-hidden transition-all hover:shadow-lg hover:border-slate-300'>
          <div className='relative z-10'>
            <p className='text-[0.55rem] font-bold uppercase tracking-widest text-slate-500'>Total</p>
            <p className='text-lg font-black text-slate-800 leading-none mt-1'>{metrics.total}</p>
          </div>
          <FileText className='absolute right-[-10px] bottom-[-10px] text-slate-200 opacity-50 transition-transform group-hover:scale-110 group-hover:rotate-6 duration-500' size={60} />
        </div>

        {/* Emitidos */}
        <div className='group relative bg-emerald-50 border border-emerald-100 rounded-xl p-3 overflow-hidden transition-all hover:shadow-lg hover:border-emerald-200'>
          <div className='relative z-10'>
            <p className='text-[0.55rem] font-bold uppercase tracking-widest text-emerald-600'>Emitidos</p>
            <p className='text-lg font-black text-emerald-700 leading-none mt-1'>{metrics.success}</p>
          </div>
          <CheckCircle2 className='absolute right-[-10px] bottom-[-10px] text-emerald-200 opacity-50 transition-transform group-hover:scale-110 group-hover:rotate-6 duration-500' size={60} />
        </div>

        {/* Rechazados */}
        <div className='group relative bg-rose-50 border border-rose-100 rounded-xl p-3 overflow-hidden transition-all hover:shadow-lg hover:border-rose-200'>
          <div className='relative z-10'>
            <p className='text-[0.55rem] font-bold uppercase tracking-widest text-rose-600'>Rechazados</p>
            <p className='text-lg font-black text-rose-700 leading-none mt-1'>{metrics.rejected}</p>
          </div>
          <XCircle className='absolute right-[-10px] bottom-[-10px] text-rose-200 opacity-50 transition-transform group-hover:scale-110 group-hover:rotate-6 duration-500' size={60} />
        </div>

        {/* Pendientes de confirmación */}
        <div className='group relative bg-amber-50 border border-amber-100 rounded-xl p-3 overflow-hidden transition-all hover:shadow-lg hover:border-amber-200'>
          <div className='relative z-10'>
            <p className='text-[0.55rem] font-bold uppercase tracking-widest text-amber-600'>Pendientes</p>
            <p className='text-lg font-black text-amber-700 leading-none mt-1'>{metrics.pending}</p>
          </div>
          <Clock className='absolute right-[-10px] bottom-[-10px] text-amber-200 opacity-50 transition-transform group-hover:scale-110 group-hover:rotate-6 duration-500' size={60} />
        </div>
      </div>

      <div className='flex flex-col flex-1 min-h-0 overflow-hidden'>
        <div className='mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <h3 className='text-sm font-bold text-slate-900 leading-tight'>Detalle por documento</h3>
          <div className='relative w-full sm:w-56'>
            <Search size={14} className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400' />
            <input
              type='text'
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder='Buscar...'
              className='w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-9 pr-3 text-[0.72rem] text-slate-700 outline-none transition focus:border-primary/40 focus:ring-4 focus:ring-primary/10'
            />
          </div>
        </div>

        <div className='flex-1 flex flex-col min-h-[200px] border border-slate-200 rounded-xl shadow-sm overflow-hidden bg-white'>
          <div className='overflow-auto h-full custom-scrollbar'>
            <table className={styles.table}>
              <thead className={styles.thead}>
                <tr>
                  <th className={clsx(styles.th, 'text-left pl-6 w-[28%]')}>Cliente / Receptor</th>
                  <th className={clsx(styles.th, 'w-[18%]')}>Documento</th>
                  <th className={clsx(styles.th, 'w-[12%] cursor-pointer select-none')} onClick={toggleSortDate}>
                    <div className='flex items-center justify-center gap-1.5'>
                      <span>Fecha</span>
                      <KeyboardArrowDownIcon fontSize='inherit' className={clsx('transition-transform', sortOrder === 'desc' && 'rotate-180')} />
                    </div>
                  </th>
                  <th className={clsx(styles.th, 'w-[14%] text-right')}>Monto</th>
                  <th className={clsx(styles.th, 'w-[12%]')}>Estado</th>
                  <th className={clsx(styles.th, 'w-[16%]')}>Motivo</th>
                  <th className={clsx(styles.th, 'w-[8%]')}>Acción</th>
                </tr>
              </thead>
              <tbody className='divide-y divide-slate-50'>
                {isLoading ? (
                  <tr><td colSpan={7} className='py-20 text-center text-slate-400 font-medium'>Cargando documentos...</td></tr>
                ) : filteredDocs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className='py-20 text-center text-slate-400 font-medium'>
                      No se encontraron resultados.
                    </td>
                  </tr>
                ) : (
                  filteredDocs.map((doc, idx) => {
                    const totals = calculateTotals(doc)
                    const code = doc?.dte_type?.code || doc?.tipo_dte || '33'
                    const rowStatus = getRowStatus(doc)
                    const rowStyle = ROW_STATUS_STYLES[rowStatus]
                    const isSuccess = rowStatus === 'success'

                    return (
                      <tr key={idx} className={styles.tr}>
                        <td className={clsx(styles.td, 'text-left pl-6')}>
                          <div className='flex flex-col gap-0.5'>
                            <span className={clsx(styles.boldText, 'truncate block max-w-[240px]')} title={doc.document_receiver?.business_name}>
                              {doc.document_receiver?.business_name || 'Sin nombre'}
                            </span>
                            <span className={styles.secondaryText}>{doc.document_receiver?.rut || '—'}</span>
                          </div>
                        </td>
                        <td className={styles.td}>
                          <div className='flex flex-col items-center gap-1.5'>
                            <span className={clsx(
                              'inline-flex px-2 py-0.5 rounded-md text-[0.55rem] font-black border',
                              code === '34'
                                ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                                : 'bg-blue-50 text-blue-600 border-blue-100'
                            )}>
                              {DTE_NAMES[code] || getDteAbbreviation(code)}
                            </span>
                            <span className={styles.secondaryText}>Fila {idx + 1}</span>
                          </div>
                        </td>
                        <td className={styles.td}>
                          <span className={clsx(styles.text, 'text-center block')}>
                            {doc.date_issued ? formatDate(doc.date_issued) : '—'}
                          </span>
                        </td>
                        <td className={styles.td}>
                          <div className='flex flex-col items-end leading-none'>
                            <span className={clsx(styles.boldText, 'text-[0.8rem]')}>${formatCLP(totals.total)}</span>
                            <div className='flex flex-col text-[0.58rem] mt-1 items-end gap-0.5'>
                              <span className='text-rose-500 font-semibold'>IVA: ${formatCLP(totals.iva)}</span>
                              <span className='text-slate-400'>Neto: ${formatCLP(totals.subtotal)}</span>
                            </div>
                          </div>
                        </td>
                        <td className={styles.td}>
                          <div className='flex justify-center'>
                            <span className={clsx(
                              'inline-flex items-center px-2.5 py-1 rounded-full text-[0.6rem] font-bold tracking-wider border',
                              rowStyle.badgeClass
                            )}>
                              {rowStyle.label}
                            </span>
                          </div>
                        </td>
                        <td className={styles.td}>
                          <span className={clsx(
                            'text-[0.62rem] font-semibold italic block text-center',
                            rowStyle.motivoClass
                          )}>
                            {isSuccess ? 'Procesado' : (doc.motivo_resultado || rowStyle.label)}
                          </span>
                        </td>
                        <td className={styles.td}>
                          <div className='flex justify-center'>
                            {isSuccess ? (
                              <ActionButton label='Ver PDF' onClick={() => handleViewPdf(doc)} tone='blue'>
                                <Eye size={16} />
                              </ActionButton>
                            ) : (
                              <ActionButton label='Ver detalle' onClick={() => setPreviewDoc(doc)}>
                                <Eye size={16} />
                              </ActionButton>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className='mt-3 flex items-center gap-3 py-2 px-4 bg-slate-50/80 rounded-xl border border-slate-100'>
          <Info size={14} className='text-blue-500' />
          <p className='text-[0.64rem] text-slate-500 font-medium'>
            Este informe resume el resultado de la emisión masiva consultada.
          </p>
        </div>
      </div>

      {/* Modal RECHAZADO: PDF renderizado por la plataforma */}
      <LucaModal
        open={previewDoc != null}
        onClose={() => setPreviewDoc(null)}
        maxWidth="max-w-6xl"
        footer={
          <Button
            onClick={() => setPreviewDoc(null)}
            title="Cerrar"
            className="bg-[#0a0a2e] text-white px-8 py-1.5 rounded-full font-bold text-sm"
          />
        }
      >
        <div className="flex-1 flex flex-col min-h-0">
          <div className='flex-1 overflow-y-auto bg-slate-100 p-6 custom-scrollbar'>
            {previewDoc && <FacturaPreviewPDF document={previewDoc} currentBusiness={currentBusiness} />}
          </div>
        </div>
      </LucaModal>

      {/* Modal EMITIDO: PDF real desde el SII */}
      <LucaModal
        open={pdfModal.open}
        onClose={() => setPdfModal({ open: false, url: null, loading: false, error: null })}
        maxWidth="max-w-6xl"
        footer={
          <Button
            onClick={() => setPdfModal({ open: false, url: null, loading: false, error: null })}
            title="Cerrar"
            className="bg-[#0a0a2e] text-white px-8 py-1.5 rounded-full font-bold text-sm"
          />
        }
      >
        <div className="flex flex-col w-full h-[80vh]">
          <div className='flex-1 overflow-hidden bg-slate-100 p-6 flex items-center justify-center'>
            {pdfModal.loading && <p>Cargando PDF...</p>}
            {pdfModal.error && <p className='text-rose-500 font-bold'>{pdfModal.error}</p>}
            {pdfModal.url && (
              <iframe
                src={pdfModal.url}
                title='PDF del documento'
                className='w-full h-full rounded-xl border-0 shadow-md'
              />
            )}
          </div>
        </div>
      </LucaModal>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; border: 2px solid #f1f1f1; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>
    </div>
  )
}
